import { Optional } from "../Types";
import { lxIsStringEmpty } from "../Utils";
import { LxI18NResource } from "./LxI18N.model";

type LxI18NResourcePartial = Optional<LxI18NResource, "namespace">;

/**
 * Creates a translation resource.
 *
 * `makeLxTranslations` creates a translation resource that can be
 * used with the `useLxTranslations` hook and
 * can be extracted with the tool `lxo-i18n-extract`.
 *
 * The `namespace` property is optional. If not provided, the `namespace` will default
 * to the `name` property in the `package.json` file.
 *
 * 🚨 Note: the `namespace` property cannot contain `.` (dot) characters.
 *
 * The `lxo-i18n-extract` tool will extract the translation resources defined with `makeLxTranslations`
 * from the source code and creates  `json` files in the `src/translations` directory.
 * See `lxo-i18n-extract` documentation for more information.
 *
 * @package I18N
 * @example
 *
 * //
 * // using the default namespace (see `lxo-i18n-extract` documentation)
 * //
 * const MY_TRANSLATIONS = makeLxTranslations({
 *  translations: {
 *   "hello": "Hallo",
 *   "world": "Welt"
 *  }
 * })
 *
 * //
 * // or using a custom namespace
 * //
 * const MY_TRANSLATIONS = makeLxTranslations({
 *  namespace: "lxo-my-app-custom-namespace",
 *  translations: {
 *   "hello": "Hallo",
 *   "world": "Welt"
 *  }
 * })
 *
 * function MyComponent() {
 *  const { t } = useLxTranslations(MY_TRANSLATIONS);
 *
 *  return ( <div>{t("hello")} {t("world")}</div> );
 * }
 */
export function makeLxTranslations<T extends LxI18NResourcePartial>(res: T) {
  if (res.namespace !== null && res.namespace !== undefined) {
    const { namespace } = res;
    if (lxIsStringEmpty(namespace)) {
      throw new Error(
        "makeLxTranslations: The `namespace` property cannot be empty."
      );
    }

    if (namespace.indexOf(".") > -1) {
      throw new Error(
        'makeLxTranslations: The `namespace` property cannot contain ".":' +
          namespace
      );
    }
  }

  return res;
}
